<template>
  <div>
    <v-card>
      <v-card-title class="text-h6 px-4 pt-4 text-left">
        Services Metadata
      </v-card-title>

      <!-- Filter Section -->
      <div class="d-flex align-center justify-space-between pa-4">
        <v-text-field
          v-model="searchFilter"
          prepend-inner-icon="mdi-magnify"
          label="Filter services and descriptions..."
          variant="outlined"
          density="compact"
          rounded="0"
          hide-details
          clearable
          class="max-width-400"
          @update:model-value="filterItems"
        ></v-text-field>

        <v-btn
          @click="openDialog()"
          color="success"
          rounded="0"
          elevation="2"
          title="New Metadata"
        >
          New Metadata
        </v-btn>
      </div>

      <v-data-table
        :headers="headers"
        :items="filteredItems"
        :loading="loading"
        item-value="id"
        class="elevation-1"
        density="compact"
      >
        <template v-slot:[`item.icon`]="{ item }">
          <v-icon :color="item.colour || 'grey'">
            {{ `mdi-${item.icon}` || "mdi-tag" }}
          </v-icon>
        </template>

        <template v-slot:[`item.plot_on_map`]="{ item }">
          <v-icon :color="item.plot_on_map ? 'success' : 'grey'">
            {{ item.plot_on_map ? "mdi-map-marker" : "mdi-map-marker-off" }}
          </v-icon>
        </template>

        <template v-slot:[`item.custom`]="{ item }">
          <v-icon :color="item.custom ? 'success' : 'grey'">
            {{ item.custom ? "mdi-check-circle" : "mdi-close-circle" }}
          </v-icon>
        </template>

        <template v-slot:[`item.live`]="{ item }">
          <v-icon :color="item.live ? 'success' : 'grey'">
            {{ item.live ? "mdi-check-circle" : "mdi-close-circle" }}
          </v-icon>
        </template>

        <template v-slot:[`item.sd_group_id`]="{ item }">
          {{ getGroupName(item.sd_group_id) }}
        </template>

        <template v-slot:[`item.reference_date`]="{ item }">
          {{
            item.reference_date
              ? new Date(item.reference_date).toLocaleDateString("en-GB")
              : ""
          }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            icon
            flat
            rounded="0"
            class="mr-1"
            @click="editItem(item)"
            title="Edit Service Metadata"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            icon
            flat
            rounded="0"
            class="mr-1"
            @click="duplicateItem(item)"
            title="Duplicate Service Metadata"
          >
            <v-icon color="primary">mdi-content-copy</v-icon>
          </v-btn>
          <v-btn
            icon
            flat
            rounded="0"
            class="ml-1"
            @click="confirmDelete(item)"
            title="Delete Service Metadata"
          >
            <v-icon color="red">mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="700">
      <v-card>
        <v-card-title
          :style="
            'background-color: ' +
            this.$store.state.config.siteConfig.toolbar_colour
          "
          class="text-white text-center"
        >
          Confirm Service Metadata Deletion?
        </v-card-title>
        <v-card-text class="text-center">
          Are you sure you want to delete the service metadata "{{
            itemToDelete?.service
          }}"?
          <v-alert type="warning" class="ma-4">
            This will also delete any Services associated with this metadata.
          </v-alert>
          <div
            style="width: 100%"
            class="text-center mt-5 text-red font-weight-bold"
          >
            This action cannot be undone!!
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="grey"
            variant="outlined"
            rounded="0"
            @click="deleteDialog = false"
            class="rounded-0"
          >
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            variant="outlined"
            rounded="0"
            @click="deleteItem(itemToDelete?.id)"
            :disabled="isDeleting"
            class="rounded-0"
          >
            <v-progress-circular
              v-if="isDeleting"
              indeterminate
              size="20"
              width="2"
              class="mr-2"
            ></v-progress-circular>
            {{ isDeleting ? "Deleting..." : "Delete" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog -->
    <v-dialog v-model="dialog" max-width="600px">
      <v-card style="height: 80vh; display: flex; flex-direction: column">
        <v-card-title
          :style="{
            backgroundColor: $store.state.config.siteConfig.toolbar_colour,
            position: 'sticky',
            top: 0,
            zIndex: 2,
          }"
          class="text-white"
        >
          {{
            editedItem.id
              ? "Edit Service Metadata"
              : "Create New Service Metadata"
          }}
        </v-card-title>
        <v-card-text style="overflow-y: auto; flex: 1 1 auto; min-height: 0">
          <v-form ref="form" v-model="isFormValid">
            <v-text-field
              v-model="editedItem.service"
              label="Service Metadata Name"
              variant="outlined"
              density="compact"
              rounded="0"
              :rules="[
                (v) => !!v || 'Service Metadata name is required',
                (v) =>
                  v.length <= 100 ||
                  'Service Metadata name must be less than 100 characters',
              ]"
              :error-messages="fieldErrors.service"
              @input="fieldErrors = {}"
              required
            />
            <v-textarea
              v-model="editedItem.description"
              label="Description"
              variant="outlined"
              density="compact"
              rounded="0"
              auto-grow
              rows="3"
              :rules="[
                (v) => !!v || 'Description is required',
                (v) =>
                  v.length <= 5000 ||
                  'Description must be less than 5000 characters',
              ]"
              :error-messages="fieldErrors.description"
              @input="fieldErrors = {}"
              required
            />
            <IconSelection
              v-model="editedItem.icon"
              :icons="icons"
              button-text="Select Icon"
              button-variant="outlined"
              button-density="compact"
              button-rounded="0"
              :required="true"
              :error-messages="fieldErrors.icon ? [fieldErrors.icon] : []"
              @update:modelValue="fieldErrors = {}"
              class="mb-6"
            />
            <v-text-field
              v-model="editedItem.colour"
              label="Colour (e.g. red, blue)"
              variant="outlined"
              density="compact"
              rounded="0"
              :rules="[
                (v) => !!v || 'Colour is required',
                (v) =>
                  /^#([0-9a-fA-F]{6})$/.test(v) ||
                  'Must be a valid 6-digit hex colour code (e.g. #FF5733)',
              ]"
              :error-messages="fieldErrors.colour"
              @input="
                editedItem.colour = editedItem.colour.toLowerCase();
                tempColor = editedItem.colour.toLowerCase();
                fieldErrors = {};
              "
              required
            >
              <template v-slot:append>
                <div
                  class="color-preview"
                  :style="{
                    backgroundColor: editedItem.colour || '#FFFFFF',
                    width: '24px',
                    height: '24px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                  }"
                  @click="showColorPicker = true"
                ></div>
              </template>
            </v-text-field>
            <v-dialog v-model="showColorPicker" max-width="300">
              <v-card>
                <v-card-text class="pa-0">
                  <v-color-picker
                    v-model="tempColor"
                    hide-inputs
                    mode="hex"
                  ></v-color-picker>
                </v-card-text>
                <v-card-actions>
                  <v-btn
                    color="error"
                    variant="text"
                    rounded="0"
                    @click="cancelColorPicker"
                  >
                    Cancel
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn
                    color="primary"
                    variant="text"
                    rounded="0"
                    @click="closeColorPicker"
                  >
                    Select
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-select
              v-model="editedItem.sd_group_id"
              label="Group"
              variant="outlined"
              density="compact"
              rounded="0"
              :items="groups"
              item-title="name"
              item-value="id"
              :rules="[(v) => !!v || 'Group is required']"
              :error-messages="fieldErrors.sd_group_id"
              @update:model-value="fieldErrors = {}"
              required
            />
            <v-textarea
              v-model="editedItem.warnings"
              label="Warnings"
              variant="outlined"
              density="compact"
              rounded="0"
              auto-grow
              rows="3"
              :rules="[
                (v) =>
                  !v ||
                  v.length <= 5000 ||
                  'Warnings must be less than 5000 characters',
              ]"
              :error-messages="fieldErrors.warnings"
              @input="fieldErrors = {}"
            />
            <v-text-field
              v-model="editedItem.source"
              label="Source"
              variant="outlined"
              density="compact"
              rounded="0"
              :rules="[
                (v) =>
                  !v ||
                  v.length <= 500 ||
                  'Source must be less than 500 characters',
              ]"
              :error-messages="fieldErrors.source"
              @input="fieldErrors = {}"
            />
            <v-text-field
              v-model="editedItem.reference_date"
              label="Reference Date"
              type="date"
              variant="outlined"
              density="compact"
              rounded="0"
              :rules="[(v) => !!v || 'Reference date is required']"
              :error-messages="fieldErrors.reference_date"
              @input="fieldErrors = {}"
              required
            />
            <v-switch
              v-model="editedItem.plot_on_map"
              label="Plot on Map?"
              density="compact"
              color="success"
            />
            <v-switch
              v-model="editedItem.custom"
              label="Custom?"
              density="compact"
              color="success"
            />
            <v-switch
              v-model="editedItem.live"
              label="Live?"
              density="compact"
              color="success"
            />
          </v-form>
          <div v-if="isLoadingColumnConfig" class="text-center pa-8">
            <v-progress-circular
              indeterminate
              size="48"
              width="4"
              color="primary"
              class="mb-4"
            ></v-progress-circular>
            <div class="text-body-2 text-grey">
              Loading column configuration...
            </div>
          </div>

          <div v-else-if="columnConfig && Object.keys(columnConfig).length > 0">
            <v-card-title class="text-h6 pa-0 mb-3">
              Column Configuration
            </v-card-title>
            <v-card-subtitle class="pa-0 mb-4">
              Drag to reorder columns and configure their display options
            </v-card-subtitle>

            <!-- Select All Controls -->
            <div
              class="d-flex align-center mb-4 pa-3 bg-grey-lighten-5 rounded"
            >
              <div class="text-subtitle-2 font-weight-medium mr-6">
                Select All:
              </div>

              <v-checkbox
                v-model="selectAllUI"
                label="UI"
                density="compact"
                hide-details
                class="mr-4"
                @change="toggleAllUI"
                data-testid="select-all-ui"
              />

              <v-checkbox
                v-model="selectAllDownload"
                label="Download"
                density="compact"
                hide-details
                @change="toggleAllDownload"
                data-testid="select-all-download"
              />
            </div>

            <div class="column-config-container">
              <Draggable
                v-model="draggableColumnConfig"
                item-key="label"
                @change="updateColumnOrder"
                class="column-list"
              >
                <template #item="{ element }">
                  <v-card
                    class="mb-2 column-item"
                    variant="outlined"
                    :class="{ dragging: false }"
                    :data-testid="`column-item-${element.label}`"
                  >
                    <v-card-text class="pa-3">
                      <div class="d-flex align-center">
                        <!-- Drag Handle -->
                        <v-icon
                          class="mr-3 drag-handle"
                          color="grey"
                          size="small"
                        >
                          mdi-drag
                        </v-icon>

                        <!-- Column Label -->
                        <div class="flex-grow-1">
                          <div class="text-subtitle-2 font-weight-medium">
                            {{ element.label }}
                          </div>
                          <div class="text-caption text-grey">
                            Order: {{ element.order }}
                          </div>
                        </div>

                        <!-- UI Checkbox -->
                        <v-checkbox
                          v-model="element.ui"
                          label="UI"
                          density="compact"
                          hide-details
                          class="mr-3"
                          @change="updateColumnConfig"
                        />

                        <!-- Download Checkbox -->
                        <v-checkbox
                          v-model="element.download"
                          label="Download"
                          density="compact"
                          hide-details
                          @change="updateColumnConfig"
                        />
                      </div>
                    </v-card-text>
                  </v-card>
                </template>
              </Draggable>
            </div>
          </div>

          <div v-else class="text-center pa-4">
            <v-icon size="large" color="grey">mdi-table-column</v-icon>
            <div class="text-body-2 text-grey mt-2">
              No column configuration available
            </div>
          </div>
        </v-card-text>
        <v-card-actions
          style="position: sticky; bottom: 0; z-index: 2; background: white"
          class="pa-3"
        >
          <v-btn
            color="grey"
            variant="outlined"
            rounded="0"
            @click="closeDialog"
            class="rounded-0"
          >
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="outlined"
            rounded="0"
            @click="saveItem"
            :disabled="!isFormValid || isSaving"
            class="rounded-0"
          >
            <v-progress-circular
              v-if="isSaving"
              indeterminate
              size="20"
              width="2"
              class="mr-2"
            ></v-progress-circular>
            {{ isSaving ? "Saving..." : "Save" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import IconSelection from "./IconSelection.vue";
import Draggable from "vuedraggable";

export default {
  name: "ServicesMetadata",
  components: {
    IconSelection,
    Draggable,
  },
  data() {
    return {
      items: [],
      groups: [],
      icons: [],
      loading: false,
      dialog: false,
      deleteDialog: false,
      itemToDelete: null,
      editedItem: {},
      tempColor: "",
      showColorPicker: false,
      columnConfig: [],
      draggableColumnConfig: [], // Data property for draggable items
      isLoadingColumnConfig: false,
      selectAllUI: false,
      selectAllDownload: false,
      searchFilter: "",
      isFormValid: false,
      fieldErrors: {},
      isSaving: false,
      isDeleting: false,
      headers: [
        { title: "Icon", value: "icon", sortable: false, align: "center" },
        { title: "Service", value: "service", sortable: true, align: "center" },
        {
          title: "Description",
          value: "description",
          sortable: true,
          align: "center",
        },
        {
          title: "Group",
          value: "sd_group_id",
          sortable: true,
          align: "center",
        },
        {
          title: "Reference Date",
          value: "reference_date",
          sortable: true,
          align: "center",
        },
        {
          title: "Plot on Map",
          value: "plot_on_map",
          sortable: true,
          align: "center",
        },
        { title: "Custom", value: "custom", sortable: true, align: "center" },
        { title: "Live", value: "live", sortable: true, align: "center" },
        {
          title: "Actions",
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
    };
  },
  computed: {
    getGroupName() {
      return (groupId) => {
        const group = this.groups.find((g) => g.id === groupId);
        return group ? group.name : "Invalid Group";
      };
    },
    columnConfigArray() {
      if (!this.columnConfig || typeof this.columnConfig !== "object") {
        return [];
      }

      // Convert associative array to array and sort by order
      const sortedArray = Object.values(this.columnConfig).sort((a, b) => {
        // Put items with order = 0 at the end
        if (a.order === 0 && b.order === 0) return 0;
        if (a.order === 0) return 1;
        if (b.order === 0) return -1;
        return a.order - b.order;
      });

      // Return a deep copy to make it mutable
      return JSON.parse(JSON.stringify(sortedArray));
    },
    // Computed properties for select all functionality
    allUISelected() {
      return (
        this.draggableColumnConfig.length > 0 &&
        this.draggableColumnConfig.every((item) => item.ui)
      );
    },
    allDownloadSelected() {
      return (
        this.draggableColumnConfig.length > 0 &&
        this.draggableColumnConfig.every((item) => item.download)
      );
    },
    // Filter items based on search term
    filteredItems() {
      if (!this.searchFilter || this.searchFilter.trim() === "") {
        return this.items;
      }

      const searchTerm = this.searchFilter.toLowerCase().trim();
      return this.items.filter((item) => {
        const serviceMatch = (item.service || "")
          .toLowerCase()
          .includes(searchTerm);
        const descriptionMatch = (item.description || "")
          .toLowerCase()
          .includes(searchTerm);
        return serviceMatch || descriptionMatch;
      });
    },
  },
  watch: {
    columnConfig: {
      handler(newVal) {
        if (newVal && typeof newVal === "object") {
          // Update the draggable array when columnConfig changes
          this.draggableColumnConfig = this.columnConfigArray;
        }
      },
      immediate: true,
    },
    // Update select all checkboxes when draggable config changes
    draggableColumnConfig: {
      handler() {
        this.selectAllUI = this.allUISelected;
        this.selectAllDownload = this.allDownloadSelected;
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchItems();
    this.fetchGroups();
    this.fetchIcons();
  },
  methods: {
    emitMessage(payload) {
      this.emit.emit("systemMessage", payload);
    },
    fetchItems() {
      this.loading = true;
      this.$axios
        .get("/sd-services-metadata")
        .then((res) => {
          this.items = res.data;
        })
        .catch((err) => {
          console.error(err);
          if (err.response?.status === 403) {
            this.emitMessage({
              title: "Access Denied",
              message: "Only OCSI users can access this feature.",
              colour: "error",
              timeout: 5000,
            });
          } else {
            this.emitMessage({
              title: "Error",
              message: "Failed to fetch services.",
              colour: "error",
              timeout: 5000,
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchGroups() {
      this.$axios
        .get("/sd-services-groups")
        .then((res) => {
          this.groups = res.data;
        })
        .catch((err) => {
          console.error(err);
          this.emitMessage({
            title: "Error",
            message: "Failed to fetch groups.",
            colour: "error",
            timeout: 5000,
          });
        });
    },
    fetchIcons() {
      this.$axios
        .get("/get-map-markers")
        .then((response) => {
          this.icons = response.data;
        })
        .catch((error) => {
          console.error(error);
          this.emitMessage({
            title: "Error",
            message: "Failed to fetch icons.",
            colour: "error",
            timeout: 5000,
          });
        });
    },
    openDialog() {
      this.editedItem = {
        service: "",
        description: "",
        icon: "",
        colour: "",
        sd_group_id: null,
        warnings: "",
        source: "",
        reference_date: null,
        plot_on_map: false,
        custom: false,
        live: false,
      };
      this.tempColor = "";
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      this.editedItem = {};
      this.tempColor = "";
    },
    editItem(item) {
      this.fetchColumnConfig(item.id);
      this.editedItem = { ...item };
      this.tempColor = item.colour || "";
      this.dialog = true;
    },
    duplicateItem(item) {
      // Create a copy of the item with modifications
      this.editedItem = {
        ...item,
        id: null, // Remove ID so it creates a new record
        service: `${item.service} (copy)`,
        live: false,
      };
      this.tempColor = item.colour || "";
      this.dialog = true;
    },
    fetchColumnConfig(id) {
      this.isLoadingColumnConfig = true;
      this.columnConfig = [];
      this.$axios
        .get(`/sd-services-metadata-column-config/${id}`)
        .then((res) => {
          this.columnConfig = res.data;
        })
        .catch((err) => {
          console.error(err);
          this.emitMessage({
            title: "Error",
            message: "Failed to load column configuration.",
            colour: "error",
            timeout: 5000,
          });
        })
        .finally(() => {
          this.isLoadingColumnConfig = false;
        });
    },
    updateColumnOrder() {
      // Update order values based on new array order
      this.draggableColumnConfig.forEach((item, index) => {
        item.order = index + 1;
      });

      // Convert back to associative array and update
      this.updateColumnConfig();
    },
    updateColumnConfig() {
      if (!this.editedItem.id) return;

      // Convert array back to associative array
      const configObject = {};
      this.draggableColumnConfig.forEach((item) => {
        configObject[item.label] = item;
      });

      // Update the columnConfig data
      this.columnConfig = configObject;

      // Send update to backend
      this.$axios
        .put(`/sd-services-metadata-column-config/${this.editedItem.id}`, {
          column_config: this.draggableColumnConfig,
        })
        .then(() => {
          this.emitMessage({
            title: "Updated",
            message: "Column configuration updated successfully.",
            colour: "success",
            timeout: 3000,
          });
        })
        .catch((err) => {
          console.error(err);
          this.emitMessage({
            title: "Error",
            message: "Failed to update column configuration.",
            colour: "error",
            timeout: 5000,
          });
        });
    },
    cancelColorPicker() {
      this.tempColor = this.editedItem.colour || "";
      this.showColorPicker = false;
    },
    closeColorPicker() {
      this.editedItem.colour = this.tempColor;
      this.showColorPicker = false;
    },
    saveItem() {
      // Validate form
      if (!this.$refs.form) {
        return;
      }

      this.$refs.form.validate().then(({ valid }) => {
        if (!valid) {
          this.emitMessage({
            title: "Validation Error",
            message: "Please fill in all required fields correctly.",
            colour: "error",
            timeout: 5000,
          });
          return;
        }

        // Clear any previous field errors
        this.fieldErrors = {};

        // Show loading message
        this.emitMessage({
          title: this.editedItem.id ? "Updating" : "Creating",
          message: "Please wait...",
          colour: "warning",
          timeout: 3000,
        });

        this.isSaving = true;
        const req = this.editedItem.id
          ? this.$axios.put(
              `/sd-services-metadata/${this.editedItem.id}`,
              this.editedItem,
            )
          : this.$axios.post(`/sd-services-metadata`, this.editedItem);

        req
          .then(() => {
            this.emitMessage({
              title: this.editedItem.id ? "Updated" : "Created",
              message: `Service ${this.editedItem.service} successfully ${this.editedItem.id ? "updated" : "created"}.`,
              colour: "success",
              timeout: 3000,
            });
            this.dialog = false;
            this.editedItem = {};
            this.tempColor = "";
            this.fieldErrors = {};
            this.fetchItems();
          })
          .catch((err) => {
            console.error(err);
            let errorMessage = "Something went wrong saving the service.";

            if (err.response?.status === 403) {
              errorMessage = "Only OCSI users can access this feature.";
            } else if (err.response?.data?.errors) {
              this.fieldErrors = err.response.data.errors;
              // Get the first error message for the snackbar
              const firstError = Object.values(err.response.data.errors)[0];
              if (Array.isArray(firstError)) {
                errorMessage = firstError[0];
              }
            }

            this.emitMessage({
              title: err.response?.status === 403 ? "Access Denied" : "Error",
              message: errorMessage,
              colour: "error",
              timeout: 5000,
            });
          })
          .finally(() => {
            this.isSaving = false;
          });
      });
    },
    confirmDelete(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },
    deleteItem(id) {
      this.deleteDialog = false;
      this.isDeleting = true;
      this.emitMessage({
        title: "Deleting Service Metadata",
        message: "Please wait...",
        colour: "warning",
        timeout: 3000,
      });
      this.$axios
        .delete(`/sd-services-metadata/${id}`)
        .then(() => {
          this.emitMessage({
            title: "Deleted",
            message: `Service Metadata ID ${id} deleted successfully.`,
            colour: "success",
            timeout: 3000,
          });
          this.fetchItems();
        })
        .catch((err) => {
          console.error(err);
          if (err.response?.status === 403) {
            this.emitMessage({
              title: "Access Denied",
              message: "Only OCSI users can access this feature.",
              colour: "error",
              timeout: 5000,
            });
          } else {
            this.emitMessage({
              title: "Error",
              message: "Failed to delete service metadata.",
              colour: "error",
              timeout: 5000,
            });
          }
        })
        .finally(() => {
          this.isDeleting = false;
        });
    },
    // Toggle all UI checkboxes
    toggleAllUI() {
      this.draggableColumnConfig.forEach((item) => {
        item.ui = this.selectAllUI;
      });
      this.updateColumnConfig();
    },
    // Toggle all Download checkboxes
    toggleAllDownload() {
      this.draggableColumnConfig.forEach((item) => {
        item.download = this.selectAllDownload;
      });
      this.updateColumnConfig();
    },
    // Filter items method (called when search input changes)
    filterItems() {
      // This method is called when the search input changes
      // The actual filtering is handled by the computed property filteredItems
      // This method can be used for additional logic if needed in the future
    },
  },
};
</script>

<style scoped>
.v-data-table :deep(.v-data-table__tr) {
  transition: background-color 0.2s ease-in-out;
}

.v-data-table :deep(.v-data-table__tr:hover) {
  background-color: rgba(0, 0, 0, 0.04) !important;
  cursor: pointer;
}

.column-item {
  transition: all 0.2s ease-in-out;
}

.column-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.drag-handle {
  cursor: grab;
}

.drag-handle:active {
  cursor: grabbing;
}

.column-list {
  min-height: 50px;
}

.max-width-400 {
  max-width: 400px;
}
</style>
